import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { adminApi, apiError } from "../api";
import { resolveImg } from "../../context/SiteContext";
import CropDialog from "./CropDialog";

const FOLDERS = ["hero", "states", "destinations", "packages", "gallery", "blog", "misc"];

export default function ImagePicker({ value, onChange, folder = "misc", label = "Image" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="grid gap-2">
      <div className="text-sm font-medium">{label}</div>
      <div className="flex items-center gap-3">
        <div className="h-20 w-32 rounded-lg overflow-hidden border bg-muted grid place-items-center shrink-0">
          {value ? (
            <img src={resolveImg(value)} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="text-xs text-muted-foreground">No image</span>
          )}
        </div>
        <div className="grid gap-1.5">
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="image-picker-open">
            Choose image
          </Button>
          {value && (
            <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => onChange("")} data-testid="image-picker-clear">
              Remove
            </Button>
          )}
        </div>
      </div>
      <ImagePickerDialog open={open} onOpenChange={setOpen} defaultFolder={folder} onSelect={(url) => { onChange(url); setOpen(false); }} />
    </div>
  );
}

export function ImagePickerDialog({ open, onOpenChange, defaultFolder = "misc", onSelect }) {
  const [folder, setFolder] = useState(defaultFolder);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [cropSrc, setCropSrc] = useState(null);
  const pendingFile = useRef(null);
  const inputRef = useRef(null);

  const loadFiles = (f) => {
    setLoading(true);
    adminApi.get(`/media/list?folder=${f}`).then(({ data }) => setFiles(data)).catch(() => setFiles([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) loadFiles(folder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, folder]);

  const doUpload = async (blob, name) => {
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", blob, name);
      fd.append("folder", folder);
      const { data } = await adminApi.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      toast.success("Image uploaded & optimized (WebP generated)");
      onSelect(data.webp_url || data.url);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploading(false);
    }
  };

  const onFileChosen = (file) => {
    if (!file) return;
    pendingFile.current = file;
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="image-picker-dialog">
          <DialogHeader>
            <DialogTitle>Select an image</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="library">
            <TabsList>
              <TabsTrigger value="library" data-testid="picker-tab-library">Library</TabsTrigger>
              <TabsTrigger value="upload" data-testid="picker-tab-upload">Upload new</TabsTrigger>
            </TabsList>
            <div className="flex gap-2 flex-wrap my-3">
              {FOLDERS.map((f) => (
                <button key={f} onClick={() => setFolder(f)} className={`px-3 py-1 rounded-full text-xs border capitalize ${folder === f ? "bg-emerald-500 text-white border-emerald-500" : "hover:border-emerald-400"}`} data-testid={`picker-folder-${f}`}>
                  {f}
                </button>
              ))}
            </div>
            <TabsContent value="library">
              {loading ? (
                <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
              ) : files.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">No images in this folder yet. Use "Upload new".</div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {files.map((f) => (
                    <button key={f.name} onClick={() => onSelect(f.webp_url || f.url)} className="group relative rounded-lg overflow-hidden border hover:ring-2 ring-emerald-500 aspect-video" data-testid={`picker-file-${f.name}`}>
                      <img src={`${resolveImg(f.url)}?v=${f.modified}`} alt={f.name} className="h-full w-full object-cover" loading="lazy" />
                      <span className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">{f.name}</span>
                      <span className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-emerald-500 text-white hidden group-hover:grid place-items-center"><Check className="h-3.5 w-3.5" /></span>
                    </button>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="upload">
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFileChosen(e.dataTransfer.files?.[0]); }}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed rounded-xl p-10 text-center cursor-pointer hover:border-emerald-400 transition-colors"
                data-testid="picker-dropzone"
              >
                <UploadCloud className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                <div className="text-sm">Drag & drop an image here, or click to browse</div>
                <div className="text-xs text-muted-foreground mt-1">Auto-compressed, resized & converted to WebP · goes to "{folder}" folder</div>
                {uploading && (
                  <div className="mt-4 h-2 rounded-full bg-muted overflow-hidden max-w-xs mx-auto">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
                  </div>
                )}
              </div>
              <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFileChosen(e.target.files?.[0])} data-testid="picker-file-input" />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
      <CropDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        onCancel={() => {
          const f = pendingFile.current;
          setCropSrc(null);
          if (f) doUpload(f, f.name);
        }}
        onCropped={(blob) => {
          const f = pendingFile.current;
          setCropSrc(null);
          doUpload(blob, f?.name || "image.jpg");
        }}
      />
    </>
  );
}
